// Helpers to translate parsed calendar busy events into matrix-grid
// coordinates (date + column range). Used by builder and voter matrices.

import type { BusyEvent } from './ics-parser';
import type { FinderConfig } from './types';
import { slotCount } from './logic';

export type BusyRange = {
  /** ISO yyyy-mm-dd (local) */
  date: string;
  /** First grid column index (0-based, inside the matrix's data columns) */
  startCol: number;
  /** Exclusive end column */
  endCol: number;
  /** Optional summary for tooltip */
  summary: string;
  /** Whether the source event has an RRULE (for UI hint) */
  isRecurring: boolean;
};

/**
 * Project busy events onto the matrix grid. Each event/day intersection
 * produces one BusyRange.
 *
 * - Events outside the matrix's date list are ignored.
 * - Events outside the day-time window are ignored.
 * - Events that span midnight produce one range per day they touch.
 * - All-day events span the full day window.
 */
export function projectBusyEvents(
  events: BusyEvent[],
  config: FinderConfig,
  dates: string[],
): BusyRange[] {
  const result: BusyRange[] = [];
  const dateSet = new Set(dates);
  const totalCols = slotCount(config);

  for (const ev of events) {
    if (Number.isNaN(ev.start.getTime()) || Number.isNaN(ev.end.getTime())) continue;
    if (ev.end.getTime() <= ev.start.getTime()) continue;

    // Iterate one day at a time across the event's span.
    const cursor = new Date(ev.start);
    cursor.setHours(0, 0, 0, 0);
    let safety = 400;
    while (cursor.getTime() < ev.end.getTime() && safety-- > 0) {
      const dateIso = isoLocal(cursor);
      const dayStart = new Date(cursor);
      const dayEnd = new Date(cursor);
      dayEnd.setDate(dayEnd.getDate() + 1);

      // Window of the event WITHIN this day.
      const winStart = ev.start.getTime() > dayStart.getTime() ? ev.start : dayStart;
      const winEnd = ev.end.getTime() < dayEnd.getTime() ? ev.end : dayEnd;
      const startMinutes = winStart.getHours() * 60 + winStart.getMinutes();
      // If end falls on the next day, treat as 24:00.
      const sameDay = winEnd.getTime() <= dayEnd.getTime() &&
        winEnd.getHours() === 0 && winEnd.getMinutes() === 0
        ? false : true;
      let endMinutes = sameDay ? winEnd.getHours() * 60 + winEnd.getMinutes() : 24 * 60;
      // Edge case: 0:00 end means "midnight" → also 24:00 from this day's view.
      if (winEnd.getTime() === dayEnd.getTime()) endMinutes = 24 * 60;

      if (dateSet.has(dateIso)) {
        const clippedStart = Math.max(startMinutes, config.dayStartMinutes);
        const clippedEnd = Math.min(endMinutes, config.dayEndMinutes);
        if (clippedEnd > clippedStart) {
          const startCol = Math.floor((clippedStart - config.dayStartMinutes) / config.slotSizeMinutes);
          const endCol = Math.min(
            totalCols,
            Math.ceil((clippedEnd - config.dayStartMinutes) / config.slotSizeMinutes),
          );
          if (endCol > startCol) {
            result.push({
              date: dateIso,
              startCol,
              endCol,
              summary: ev.summary,
              isRecurring: ev.isRecurring,
            });
          }
        }
      }

      cursor.setDate(cursor.getDate() + 1);
    }
  }

  return result;
}

function isoLocal(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}
